import React, { useEffect, useState } from 'react'
import axios from 'axios'
import Select from 'react-select'
import { t } from '../../../i18n'
import { NamedRecord, SelectOption, toSelectOption } from '../selectOptions'
import StarRating from './StarRating'
import { CommentFormValues, CommentTagging } from './types'

const selectPortalProps = {
  menuPortalTarget: typeof document === 'undefined' ? null : document.body,
  styles: { menuPortal: (base: object) => ({ ...base, zIndex: 9999 }) }
}

interface CommentFormProps {
  initialValues: CommentFormValues
  initialTags: SelectOption[]
  initialOrganizationId: number | null
  tagging: CommentTagging | null
  tagOptionsUrl: string
  placeholder: string
  submitLabel: string
  onSubmit: (
    values: CommentFormValues,
    onError: (messages: string[]) => void
  ) => void
  onCancel: () => void
}

const CommentForm: React.FC<CommentFormProps> = ({
  initialValues,
  initialTags,
  initialOrganizationId,
  tagging,
  tagOptionsUrl,
  placeholder,
  submitLabel,
  onSubmit,
  onCancel
}) => {
  const [values, setValues] = useState<CommentFormValues>(initialValues)
  const [organizationId, setOrganizationId] = useState<number | null>(
    initialOrganizationId
  )
  const [selectedTags, setSelectedTags] = useState<SelectOption[]>(initialTags)
  const [tagOptions, setTagOptions] = useState<SelectOption[]>([])
  const [loadingTags, setLoadingTags] = useState(false)
  const [showTagging, setShowTagging] = useState(initialTags.length > 0)
  const [errors, setErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  const hasTagging = tagging !== null
  const byOrganization = tagging?.byOrganization ?? false

  useEffect(() => {
    if (
      !hasTagging ||
      !showTagging ||
      (byOrganization && organizationId === null)
    ) {
      setTagOptions([])
      return
    }
    setLoadingTags(true)
    setTagOptions([])
    axios
      .get<NamedRecord[]>(tagOptionsUrl, {
        params: byOrganization ? { organization_id: organizationId } : {}
      })
      .then((resp) => setTagOptions(resp.data.map(toSelectOption)))
      .catch(() => setTagOptions([]))
      .finally(() => setLoadingTags(false))
  }, [hasTagging, showTagging, byOrganization, organizationId, tagOptionsUrl])

  const submit = () => {
    setSubmitting(true)
    setErrors([])
    onSubmit(values, (messages) => {
      setErrors(messages)
      setSubmitting(false)
    })
  }

  const renderTagging = (tagging: CommentTagging) => {
    const tags = tagging.tagsName.toLowerCase()
    const organizationOptions = tagging.organizations.map(toSelectOption)

    if (!showTagging) {
      return (
        <button
          type="button"
          className="btn btn-link btn-sm ps-0"
          onClick={() => setShowTagging(true)}
        >
          + {tagging.label}
        </button>
      )
    }

    return (
      <>
        {byOrganization && (
          <div className="form-group">
            <label>{t('comments.tagging.organization')}</label>
            <Select<SelectOption>
              classNamePrefix="react-select"
              className="react-select"
              placeholder={t('comments.tagging.select_organization', { tags })}
              options={organizationOptions}
              value={
                organizationOptions.find(
                  (option) => option.value === organizationId
                ) || null
              }
              isClearable
              onChange={(option) => setOrganizationId(option?.value ?? null)}
              {...selectPortalProps}
            />
          </div>
        )}
        <div className="form-group">
          <label>{tagging.tagsName}</label>
          <Select<SelectOption, true>
            classNamePrefix="react-select"
            className="react-select"
            placeholder={t('comments.tagging.select', { tags })}
            options={tagOptions}
            value={selectedTags}
            isMulti
            isDisabled={
              byOrganization &&
              organizationId === null &&
              selectedTags.length === 0
            }
            isLoading={loadingTags}
            loadingMessage={() => t('comments.tagging.loading', { tags })}
            noOptionsMessage={() => t('comments.tagging.none', { tags })}
            onChange={(options) => {
              const picked = [...options]
              setSelectedTags(picked)
              setValues({
                ...values,
                tag_ids: picked.map((option) => option.value)
              })
            }}
            {...selectPortalProps}
          />
        </div>
      </>
    )
  }

  return (
    <div className="comment-card">
      {errors.length > 0 && (
        <div className="alert alert-danger" role="alert">
          {errors.join(', ')}
        </div>
      )}
      <div className="form-group">
        <label>{t('comments.rating')}</label>
        <div>
          <StarRating
            value={values.rating}
            size={24}
            onChange={(rating) => setValues({ ...values, rating })}
          />
        </div>
      </div>
      <div className="form-group">
        <label>{t('comments.comment')}</label>
        <textarea
          className="form-control"
          rows={3}
          value={values.body}
          placeholder={placeholder}
          onChange={(e) => setValues({ ...values, body: e.target.value })}
        />
      </div>
      {tagging && renderTagging(tagging)}
      <div className="d-flex justify-content-end mt-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm inverted me-2"
          onClick={onCancel}
        >
          {t('cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={
            submitting || values.rating === 0 || values.body.trim() === ''
          }
          onClick={submit}
        >
          {submitLabel}
        </button>
      </div>
    </div>
  )
}

export default CommentForm
